import { FC, useCallback, useEffect, useMemo } from 'react';
import { withDialogSuspenseErrorBoundary } from '@retail/components';
import { ContainedButton, Dialog, TextButton } from '@shared/components';
import { Alert, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { useToast } from '@retail/hooks';
import { useImportAgreementPricesStore } from '@retail/purchase-agreements/stores';
import { usePostPurchaseAgreements } from '@retail/suppliers/data-access';
import { Supplier } from '@retail/suppliers/types';
import { useAppTFunction } from '@retail/app/i18n';
import { useSelectedOrgUnit, useUserBrands } from '@retail/app/stores/selected-context';
import { SpreadSheetRow, UploadContainer } from '@retail/products/components';
import { downloadTemplateCsv } from '@retail/products/utils';

interface Props {
  open: boolean;
  onClose: () => void;
  supplier: Supplier;
}

const csvDataNo = '\uFEFFMgItem-nummer;Nobb-nummer;Purchase-price;From-date;To-date;GTIN';
const csvDataSe = '\uFEFFMgItem-nummer;Finfo-nummer;Purchase-price;From-date;To-date;GTIN';

const AgreementsUploadDialog: FC<Props> = ({ open, onClose, supplier }) => {
  const toast = useToast();
  const t = useAppTFunction();
  const { appTenant } = useSelectedOrgUnit();
  const {
    selectedBrand: { wholesaler },
  } = useUserBrands();
  const { importedItemPrices, rejectedRows, importedFile, importSpreadsheet, clearState } =
    useImportAgreementPricesStore((state) => state);

  const { mutateAsync: postPurchaseAgreementPrices, isPending } = usePostPurchaseAgreements({
    localAgreement: !wholesaler,
    supplierNumber: supplier.mgSupplierId,
  });

  useEffect(() => clearState, [clearState]);

  const onSubmitPurchaseAgreementPrices = useCallback(() => {
    postPurchaseAgreementPrices({
      body: importedItemPrices ?? [],
      urlVariables: supplier.mgSupplierId,
    })
      .then(() => {
        toast.success(t('purchaseAgreements.agreement.prices.upload.success.import'));
        onClose();
      })
      .catch((e) => {
        console.error(e);
        toast.error(t('purchaseAgreements.agreement.prices.upload.error.api') + e.payload?.message);
      });
  }, [importedItemPrices, onClose, postPurchaseAgreementPrices, supplier.mgSupplierId, t, toast]);

  const importedAllRowsSuccessfully = useMemo(
    () => !!importedItemPrices?.length && !rejectedRows?.length,
    [importedItemPrices?.length, rejectedRows?.length]
  );

  return (
    <Dialog open={open} onClose={onClose} fullScreen sx={{ padding: 4 }}>
      <DialogTitle>
        {t('purchaseAgreements.agreement.prices.upload.titleSupplier', {
          supplierName: supplier.name,
        })}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} alignItems="center" mt={8}>
          <UploadContainer
            importedFile={importedFile}
            onDownloadTemplate={() =>
              downloadTemplateCsv(
                appTenant.countryCode === 'SE' ? csvDataSe : csvDataNo,
                'agreementsTemplate.csv'
              )
            }
            importSpreadsheet={(rows: SpreadSheetRow[], file: File) =>
              importSpreadsheet(rows, file, appTenant.countryCode)
            }
            resetImportedStates={clearState}
          />

          {!!importedItemPrices?.length && !rejectedRows?.length && (
            <Alert severity="success">
              {t('purchaseAgreements.agreement.prices.upload.success.fileContent', {
                count: importedItemPrices.length,
              })}
            </Alert>
          )}
          {!!rejectedRows?.length && (
            <Alert severity="error">
              {t('purchaseAgreements.agreement.prices.upload.error.fileContent', {
                count: rejectedRows.length,
                rowNumbers: rejectedRows.map(({ index }) => index + 1).join(', '),
              })}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <TextButton variant="text" size="small" onClick={onClose} loading={isPending}>
          {t('common.cancel')}
        </TextButton>
        <ContainedButton
          onClick={onSubmitPurchaseAgreementPrices}
          size="small"
          disabled={!importedAllRowsSuccessfully}
          loading={isPending}
        >
          {t('purchaseAgreements.agreement.prices.upload.importAction')}
        </ContainedButton>
      </DialogActions>
    </Dialog>
  );
};

export default withDialogSuspenseErrorBoundary(AgreementsUploadDialog);
